import { FC, PropsWithChildren, useMemo } from 'react';

import { OrganizationApiProvider } from '@gasbi/shared/web/Organization';
import { useDispatch } from 'react-redux';

import { useGetDepartmentsIInfiniteQuery } from '@/features/departments';
import { useGetEmployeePositionsIInfiniteQuery } from '@/features/employeePositions';
import {
  employeesApi,
  useGetEmployeeByIdQuery,
  useGetEmployeesAccessibleByOrganizationQuery,
  useGetEmployeesByRoleInfiniteQuery,
} from '@/features/employees';
import {
  mainOrganizationApi,
  useGetMainOrganizationRolesInfiniteQuery,
} from '@/features/mainOrganization';
import {
  organizationsApi,
  useGetCachedOrganizationByIdSelector,
  useGetOrganizationByIdQuery,
  useGetOrganizationRolesInfiniteQuery,
  useGetOrganizationsInfiniteQuery,
} from '@/features/organizations';
import { permissionsApi } from '@/features/permissions';
import {
  rolesApi,
  useAssignRoleMutation,
  useGetCachedRoleByIdSelector,
  useGetRoleAvailablePermissionsByIdQuery,
  useGetRoleByIdQuery,
  useGetRolePermissionsByIdQuery,
  useGetSystemRolesQuery,
  useSaveRoleMutation,
  useSuspendRoleMutation,
} from '@/features/roles';
import { RootRoleId } from '@/features/roles/types';
import { getRoleLabel, getRoleType, isRootRole } from '@/features/roles/utils';
import {
  subdivisionsApi,
  useGetSubdivisionsInfiniteQuery,
} from '@/features/subdivisions';

const getOrganizationApiConfig = dispatch => ({
  organizationsApi: {
    ...organizationsApi,
    useGetOrganizationByIdQuery,
    useGetOrganizationRolesInfiniteQuery,
    useGetCachedOrganizationByIdSelector,
    useGetOrganizationsInfiniteQuery,
  },
  employeesApi: {
    ...employeesApi,
    useGetEmployeeByIdQuery,
    useGetDepartmentsIInfiniteQuery,
    useGetEmployeePositionsIInfiniteQuery,
    useGetEmployeesAccessibleByOrganizationQuery,
    useGetEmployeesByRoleInfiniteQuery,
    canReactivateEmployee: true,
  },
  subdivisionsApi: {
    ...subdivisionsApi,
    useGetSubdivisionsInfiniteQuery,
  },
  mainOrganizationApi: {
    ...mainOrganizationApi,
    useGetMainOrganizationRolesInfiniteQuery,
  },
  rolesApi: {
    ...rolesApi,
    useGetSystemRolesQuery,
    useGetRoleByIdQuery,
    useGetCachedRoleByIdSelector,
    useAssignRoleMutation,
    useSuspendRoleMutation,
    useSaveRoleMutation,
    useGetRolePermissionsByIdQuery,
    useGetRoleAvailablePermissionsByIdQuery,
  },
  common: {
    RootRoleId,
    getRoleLabel,
    getRoleType,
    isRootRole,
  },
  utils: {
    invalidateOrgRolesTags: () =>
      dispatch(organizationsApi.util.invalidateTags(['Roles'])),
    invalidateMainOrgRolesTags: () =>
      dispatch(mainOrganizationApi.util.invalidateTags(['Roles'])),
    invalidateEmployeesByRoleTags: () =>
      dispatch(employeesApi.util.invalidateTags(['EmployeesByRole'])),
    invalidateUserPermissionsTags: () =>
      dispatch(permissionsApi.util.invalidateTags(['UserPermissions'])),
  },
});

export const OrganizationApiContextWrapper: FC<PropsWithChildren> = ({
  children,
}) => {
  const dispatch = useDispatch();

  const config = useMemo(() => getOrganizationApiConfig(dispatch), [dispatch]);

  return (
    <OrganizationApiProvider value={config}>{children}</OrganizationApiProvider>
  );
};
